import { useState } from "react";
import Modal from "../Modal";
import dayjs from "dayjs";
import classes from "../Modal/Modal.module.scss";
import { useAppDispatch } from "../../../../hooks/redux";
import CustomDatePicker from "../../../generic/dataEntry/CustomDatePicker/CustomDatePicker";
import $api from "../../../../axios";
import { notification, Select } from "antd";

interface IModalExportOrdersProps {
  close: () => void;
}

function ModalExportOrders({ close }: IModalExportOrdersProps) {
  const dispatch = useAppDispatch();
  const [startDate, setStartDate] = useState<string>(
    dayjs().format("YYYY-MM-DD")
  );
  const [endDate, setEndDate] = useState<string>(dayjs().format("YYYY-MM-DD"));
  const [exportType, setExportType] = useState<"month" | "year" | "range">(
    "month"
  );
  const [isLoading, setIsLoading] = useState(false);
  const [api, contextHolder] = notification.useNotification();

  const handleExport = async () => {
    if (!startDate) return;

    setIsLoading(true);

    try {
      let params = {};

      if (exportType === "month") {
        params = {
          month: dayjs(startDate).month() + 1,
          year: dayjs(startDate).year(),
        };
      } else if (exportType === "year") {
        params = {
          year: dayjs(startDate).year(),
        };
      } else if (exportType === "range") {
        if (!endDate) return;
        params = {
          start_date: startDate,
          end_date: endDate,
        };
      }

      const response = await $api.get("/request/request/export-requests", {
        params,
        responseType: "blob",
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;

      const contentDisposition = response.headers["content-disposition"];
      let fileName = "export.xlsx";

      if (contentDisposition) {
        const filenameStarMatch = contentDisposition.match(
          /filename\*\=UTF-8''([^;]+)/i
        );
        if (filenameStarMatch && filenameStarMatch[1]) {
          fileName = decodeURIComponent(filenameStarMatch[1]);
        } else {
          const filenameMatch =
            contentDisposition.match(/filename="?([^"]+)"?/i);
          if (filenameMatch && filenameMatch[1]) {
            fileName = filenameMatch[1];
          }
        }
      }

      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);

      api.success({
        message: "Успешно",
        description: "Выгрузка успешно завершена",
        placement: "topRight",
      });

      close();
    } catch (error: any) {
      console.error("Export error:", error);
      api.error({
        message: "Ошибка",
        description:
          error.response?.data?.message || "Ошибка при выгрузке данных",
        placement: "topRight",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      {contextHolder}
      <Modal
        title="Выгрузка заявок в Excel"
        close={close}
        submitClick={handleExport}
        cancelClick={close}
        submitDisabled={
          isLoading || !startDate || (exportType === "range" && !endDate)
        }
        submitLoading={isLoading}
        submitText="Выгрузить"
        dialogSizeSuffix="w435"
      >
        <div className={classes.form}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              marginBottom: 16,
            }}
          >
            <label style={{ flexShrink: 0 }}>Тип выгрузки:</label>
            <Select
              value={exportType}
              onChange={(value) => setExportType(value)}
              style={{ flex: 1 }}
              options={[
                { value: "month", label: "За месяц" },
                { value: "year", label: "За год" },
              ]}
            />
          </div>

          {exportType === "month" && (
            <div className={classes.form_field}>
              <CustomDatePicker
                name="exportMonth"
                label="Выберите месяц:"
                dateFormat="MMMM YYYY"
                picker="month"
                curDate={startDate}
                onChangeDate={setStartDate}
              />
            </div>
          )}

          {exportType === "year" && (
            <div className={classes.form_field}>
              <CustomDatePicker
                name="exportYear"
                label="Выберите год:"
                dateFormat="YYYY"
                picker="year"
                curDate={startDate}
                onChangeDate={setStartDate}
              />
            </div>
          )}

          {exportType === "range" && (
            <>
              <div className={classes.form_field}>
                <CustomDatePicker
                  name="startDate"
                  label="Начальная дата:"
                  curDate={startDate}
                  onChangeDate={setStartDate}
                />
              </div>
              <div className={classes.form_field}>
                <CustomDatePicker
                  name="endDate"
                  label="Конечная дата:"
                  curDate={endDate}
                  onChangeDate={setEndDate}
                  disabledDate={(current) => {
                    return current && current < dayjs(startDate).startOf("day");
                  }}
                />
              </div>
            </>
          )}
        </div>
      </Modal>
    </>
  );
}

export default ModalExportOrders;
